import React, { useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// Normaliza texto para búsqueda insensible a acentos y mayúsculas
const _norm = (s) => {
  if (!s) return '';
  return String(s)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
};

/**
 * Campo del formulario de video para asignar ejercicios de reemplazo.
 * - Buscador en vivo (insensible a mayúsculas/acentos)
 * - Checkboxes multi-selección
 * - Chips de los seleccionados con botón X
 * - Excluye el video que se está editando (no puede ser reemplazo de sí mismo)
 */
export default function ReplacementExercisesField({ videoData, setVideoData, videosList, currentVideoId }) {
  const [query, setQuery] = useState('');

  const selectedIds = useMemo(
    () => (Array.isArray(videoData?.replacement_video_ids) ? videoData.replacement_video_ids : []),
    [videoData]
  );

  // Mapa id → título para los chips (los seleccionados pueden no estar visibles con el filtro)
  const idToTitle = useMemo(() => {
    const map = {};
    (videosList || []).forEach((v) => {
      if (v && v.id) map[v.id] = v.title || '(sin título)';
    });
    return map;
  }, [videosList]);

  const filtered = useMemo(() => {
    const q = _norm(query);
    const base = (videosList || []).filter(
      (v) => v && v.id && v.id !== currentVideoId && v.title
    );
    const sorted = [...base].sort((a, b) =>
      _norm(a.title).localeCompare(_norm(b.title))
    );
    if (!q) return sorted;
    return sorted.filter((v) => _norm(v.title).includes(q));
  }, [videosList, query, currentVideoId]);

  const toggle = (id) => {
    const next = selectedIds.includes(id)
      ? selectedIds.filter((x) => x !== id)
      : [...selectedIds, id];
    setVideoData({ ...videoData, replacement_video_ids: next });
  };

  const remove = (id) => {
    setVideoData({
      ...videoData,
      replacement_video_ids: selectedIds.filter((x) => x !== id),
    });
  };

  return (
    <div
      className="space-y-3 p-4 rounded-lg border-2 border-cyan-500/30 bg-cyan-500/10"
      data-testid="replacement-exercises-field"
    >
      <Label className="text-cyan-300 font-bold">🔁 Ejercicios de reemplazo</Label>
      <p className="text-xs text-gray-400 leading-snug">
        Elegí uno o varios videos que la clienta pueda usar como alternativa cuando no pueda hacer este ejercicio.
        Si no asignás ninguno, el botón de alternativas no aparece en su rutina.
      </p>

      {/* Chips de seleccionados */}
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="replacement-chips">
          {selectedIds.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/20 border border-cyan-400/40 text-xs text-cyan-100"
              data-testid={`replacement-chip-${id}`}
            >
              {idToTitle[id] || `(video ${id})`}
              <button
                type="button"
                onClick={() => remove(id)}
                className="w-4 h-4 rounded-full bg-cyan-400/30 hover:bg-cyan-400/60 flex items-center justify-center text-cyan-50 leading-none"
                title="Quitar"
                data-testid={`replacement-chip-remove-${id}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Buscador */}
      <Input
        type="text"
        placeholder="Buscar por nombre…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        data-testid="replacement-search-input"
      />

      {/* Lista con checkboxes */}
      <div
        className="max-h-60 overflow-y-auto rounded border border-gray-700 bg-gray-900/40 divide-y divide-gray-800"
        data-testid="replacement-list"
      >
        {filtered.length === 0 ? (
          <p className="p-3 text-xs text-gray-500">
            {query ? 'Sin resultados para esa búsqueda.' : 'No hay otros videos disponibles.'}
          </p>
        ) : (
          filtered.map((v) => {
            const checked = selectedIds.includes(v.id);
            return (
              <label
                key={v.id}
                className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm hover:bg-cyan-500/10 ${
                  checked ? 'bg-cyan-500/15' : ''
                }`}
                data-testid={`replacement-option-${v.id}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(v.id)}
                  className="w-4 h-4"
                />
                <span className="text-gray-100">
                  {v.title}
                  {v.category && (
                    <span className="text-gray-400"> ({v.category})</span>
                  )}
                </span>
                {v.muscle_group && (
                  <span className="text-[10px] text-gray-500 ml-auto">{v.muscle_group}</span>
                )}
              </label>
            );
          })
        )}
      </div>

      <p className="text-[11px] text-gray-500">
        {selectedIds.length} seleccionado{selectedIds.length === 1 ? '' : 's'}.
      </p>
    </div>
  );
}
